/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement, ReactNode, FC } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Custom components
import AIChatAssistant from "examples/AIChatAssistant";
import DemoRibbon, {
  DEMO_RIBBON_HEIGHT,
  DEMO_RIBBON_STORAGE_KEY,
} from "components/DemoRibbon";

// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React TS routes
import routes from "routes";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Authentication context
import { AuthProvider, useAuth } from "context/auth/authContext";

// Images
// import favicon from "assets/images/favicon.png"; // Unused
// import appleIcon from "assets/images/apple-icon.png"; // Unused
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";

// Account Settings
import Settings from "layouts/pages/account/settings";

// Authentication pages
import SignInBasic from "layouts/authentication/sign-in/basic";
import SignUpCover from "layouts/authentication/sign-up/cover";

// Protected Route component
interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children }) => {
  const { authState } = useAuth();
  const location = useLocation();

  // Add detailed logging
  console.log(`ProtectedRoute Check: Path=${location.pathname}, full authState:`, authState);

  // If still loading, show a spinner
  if (authState.loading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </MDBox>
    );
  }

  if (!authState.isAuthenticated) {
    // Redirect to the login page with a return to location
    console.log("ProtectedRoute: Not authenticated, redirecting to login");
    return <Navigate to="/authentication/sign-in/basic" state={{ from: location }} replace />;
  }

  console.log("ProtectedRoute: Authenticated, rendering children");
  return <>{children}</>;
};

// App content component (to avoid auth hook usage in the main App function)
const AppContent = () => {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { authState } = useAuth();
  const { isAuthenticated } = authState; // Removed user

  // Debugging for auth state
  useEffect(() => {
    console.log("AppContent auth state:", authState);
  }, [authState]);

  // Chat state
  const [chatOpen, setChatOpen] = useState<boolean>(false);

  // letsgetyou.ai demo ribbon (dismiss persists for the session)
  const [ribbonVisible, setRibbonVisible] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DEMO_RIBBON_STORAGE_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const handleRibbonDismiss = () => {
    setRibbonVisible(false);
    try {
      sessionStorage.setItem(DEMO_RIBBON_STORAGE_KEY, "1");
    } catch {
      /* storage unavailable — dismiss for this render only */
    }
  };

  // Keep the fixed Sidenav clear of the ribbon (16px frame margin preserved)
  const sidenavRibbonSx = ribbonVisible
    ? {
        "& .MuiDrawer-paper": {
          marginTop: `${DEMO_RIBBON_HEIGHT + 16}px`,
          height: `calc(100vh - ${DEMO_RIBBON_HEIGHT + 32}px)`,
        },
      }
    : undefined;

  // Cache for the rtl
  useMemo(() => {
    const pluginRtl: any = rtlPlugin;
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [pluginRtl],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Toggle chat open/close
  const handleChatOpen = () => setChatOpen(!chatOpen);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Set document title (browser tab title)
  useEffect(() => {
    document.title = "AutoCount";
  }, []);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
        protected: boolean;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          // Check if this is a protected route
          const isAuthRoute = route.route.startsWith("/authentication/");
          
          if (isAuthRoute) {
            return <Route path={route.route} element={route.component} key={route.key} />;
          }
          
          return (
            <Route
              path={route.route}
              element={<ProtectedRoute>{route.component}</ProtectedRoute>}
              key={route.key}
            />
          );
        }

        return null;
      }
    );

  const chatButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleChatOpen}
    >
      <Icon fontSize="small" color="inherit">
        chat
      </Icon>
    </MDBox>
  );

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  // Default redirect based on authentication status
  // Use a memoized value to ensure stability across renders
  const defaultRedirect = useMemo(() => {
    console.log("Calculating default redirect, isAuthenticated:", isAuthenticated);
    return isAuthenticated ? "/dashboard" : "/authentication/sign-in/basic";
  }, [isAuthenticated]);

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <DemoRibbon visible={ribbonVisible} onDismiss={handleRibbonDismiss} />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="AutoCount"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              sx={sidenavRibbonSx}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          
          {/* Authentication Routes */}
          <Route path="/authentication/sign-in/basic" element={<SignInBasic />} />
          <Route path="/authentication/sign-up/cover" element={<SignUpCover />} />
          
          {/* Account Settings Route */}
          <Route 
            path="/pages/account/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          
          <Route path="*" element={<Navigate to={defaultRedirect} />} />
        </Routes>
        {chatButton}
        <AIChatAssistant open={chatOpen} onClose={() => setChatOpen(false)} />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <DemoRibbon visible={ribbonVisible} onDismiss={handleRibbonDismiss} />
      {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="AutoCount"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              sx={sidenavRibbonSx}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          
          {/* Authentication Routes */}
          <Route path="/authentication/sign-in/basic" element={<SignInBasic />} />
          <Route path="/authentication/sign-up/cover" element={<SignUpCover />} />
          
          {/* Account Settings Route */}
          <Route 
            path="/pages/account/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          
          <Route path="*" element={<Navigate to={defaultRedirect} />} />
        </Routes>
        {chatButton}
        <AIChatAssistant open={chatOpen} onClose={() => setChatOpen(false)} />
    </ThemeProvider>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
